
import type { Action } from '@empathybroker/tsclient-play/dist/models/action'
import type { PlayErrorField } from '@empathybroker/tsclient-play/dist/models/play-error-field'
import type { PlayErrorReason } from '@empathybroker/tsclient-play/dist/models/play-error-reason'
import type { PropType } from 'vue'
import type { Filter, RegularFilter } from '../../models'

import { defineComponent } from 'vue'
import { mapGetters } from 'vuex'

import { TYPES } from '../../config/inversify.types'

import { ColorEnum, FilterTypeEnum } from '../../enums'

import { CONTEXT_CONSTS } from '../../store/context/context.consts'

import { UiAlert } from '../keen-ui'
import { EpbBadge } from '../shared/epb-badge'
import { EpbButton } from '../shared/epb-button'
import { EpbText } from '../shared/epb-text'

import EnabledField from './enabled-field.component.vue'
import Schedule from './schedule.component.vue'

export default defineComponent({
  name: 'CrudForm',
  components: {
    UiAlert,
    EpbBadge,
    EpbButton,
    EpbText,
    EnabledField,
    Schedule,
  },
  inject: {
    objectIdService: TYPES.ObjectIdService,
  },
  computed: {
    ...mapGetters(CONTEXT_CONSTS.moduleName, {
      context: CONTEXT_CONSTS.getters.context,
    }),
    enabledValue() {
      const { enabled } = this.item.criteria ?? this.item
      return enabled
    },
  },
  props: {
    item: Object as PropType<Action>,
    readOnly: Boolean,
    enabledFieldConfig: Object as PropType<{
      disabled: boolean
      alertConfig?: { message: string }
    }>,
    includeScheduleFields: Boolean,
    sectionTitlesHidden: Boolean,
    acceptActionDisabled: Boolean,
    errorsFields: Array as PropType<Array<PlayErrorField>>,
    messages: Object,
    sectionTitlesDisabled: Boolean,
  },
  setup() {
    return {
      ColorEnum,
    }
  },
  data() {
    return {
      showErrorContainer: false,
    }
  },
  watch: {
    errorsFields: {
      handler(errorsFields: any[]): void {
        this.showErrorContainer = !!errorsFields.length
        if (this.showErrorContainer) {
          this.$emit('resetScroll')
        }
      },
      immediate: true,
    },
  },
  methods: {
    getErrorFieldHeaderText(errorField: PlayErrorField): string {
      let headerText: string
      let fieldId
      if (!Array.isArray(errorField.fieldId)) {
        fieldId = errorField.fieldId.split('.').pop()
      } else {
        fieldId = errorField.fieldId
          .map(field => {
            const fieldMessage = this.messages.fields[field.split('.').pop()]
            return (!!fieldMessage && fieldMessage.label) || field
          })
          .join(', ')
      }
      const fieldMessages = this.messages.fields[fieldId]
      if (fieldMessages) {
        headerText = fieldMessages.label
      } else {
        const fieldFilter = Object.values(this.context.filters).find(
          (filter: Filter) =>
            filter.config.type === FilterTypeEnum.REGULAR &&
            ((filter as RegularFilter).config.toolingId || filter.config.id) === fieldId,
        ) as RegularFilter
        if (fieldFilter) {
          const filtersNames = this.$epb.messages.filters.names
          headerText =
            fieldFilter.config.prettyName ||
            filtersNames[fieldFilter.config.id] ||
            fieldFilter.config.id
        }
      }
      return headerText || fieldId
    },
    getErrorFieldReasonMessage(reason: PlayErrorReason, errorField: PlayErrorField): string {
      let reasonMessage: string
      let fieldId: string
      if (!Array.isArray(errorField.fieldId)) {
        fieldId = errorField.fieldId.split('.').pop()
      } else {
        fieldId = errorField.fieldId.map(field => field.split('.').pop()).join(', ')
      }
      const fieldMessages = this.messages.fields[fieldId]
      if (!!fieldMessages && !!fieldMessages.errors) {
        reasonMessage = fieldMessages.errors[reason.id]
      }
      return reasonMessage || this.$epb.messages.errors[400].reasons[reason.id] || reason.message
    },
    setItemEnabled(enabled: boolean): void {
      const itemEnableObj = this.item.criteria ?? this.item
      itemEnableObj.enabled = enabled
    },
  },
})
